"use client"

import { useState, useEffect } from "react"
import { Link, useNavigate } from "react-router-dom"
import axios from "axios"
import { API_URL } from "../config"
import { useLanguage } from "../context/LanguageContext"
import { useNotification } from "../context/NotificationContext"
import "./ProjectManagement.css"

const ProjectManagement = () => {
  const { t, direction } = useLanguage()
  const { showNotification } = useNotification()
  const navigate = useNavigate()

  const [projects, setProjects] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [sortBy, setSortBy] = useState("createdAt")
  const [sortOrder, setSortOrder] = useState("desc")
  const [statusFilter, setStatusFilter] = useState("all") // "all", "active", "completed", "planning", "paused"
  const [farms, setFarms] = useState([])
  const [selectedFarm, setSelectedFarm] = useState("all")

  useEffect(() => {
    fetchProjects()
    fetchFarms()
  }, [sortBy, sortOrder, statusFilter, selectedFarm])

  const fetchProjects = async () => {
    try {
      setLoading(true)
      const response = await axios.get(`${API_URL}/api/projects`)
      setProjects(response.data)
      setLoading(false)
    } catch (err) {
      setError("Failed to load Fields")
      setLoading(false)
      console.error(err)
    }
  }

  const fetchFarms = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/farms?active=true`)
      setFarms(response.data)
    } catch (err) {
      console.error("Error fetching farms:", err)
    }
  }

  const handleDeleteProject = async (projectId, projectName) => {
    if (!window.confirm(t(`Are you sure you want to delete "${projectName}"? This action cannot be undone.`))) {
      return
    }

    try {
      await axios.delete(`${API_URL}/api/projects/${projectId}`)
      showNotification(t("Field deleted successfully"), "success")
      fetchProjects() // Refresh the list
    } catch (err) {
      console.error("Error deleting Field:", err)
      showNotification(err.response?.data?.msg || "Failed to delete Field", "error")
    }
  }

  const handleStatusChange = async (projectId, newStatus) => {
    try {
      await axios.patch(`${API_URL}/api/projects/${projectId}/status`, { status: newStatus })
      showNotification(t("Field status updated successfully"), "success")
      fetchProjects() // Refresh the list
    } catch (err) {
      console.error("Error updating Field status:", err)
      showNotification(err.response?.data?.msg || "Failed to update Field status", "error")
    }
  }

  // Filter projects based on search term, status, and farm
  const filteredProjects = projects.filter((project) => {
    const matchesSearch =
      project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (project.description && project.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
      project.farm.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (project.cropType && project.cropType.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (project.customCropType && project.customCropType.cropType.toLowerCase().includes(searchTerm.toLowerCase()))

    const matchesStatus = statusFilter === "all" || project.status === statusFilter
    const matchesFarm = selectedFarm === "all" || project.farm._id === selectedFarm

    return matchesSearch && matchesStatus && matchesFarm
  })

  // Sort projects
  const sortedProjects = [...filteredProjects].sort((a, b) => {
    let aValue = a[sortBy]
    let bValue = b[sortBy]

    if (sortBy === "farm") {
      aValue = a.farm.name
      bValue = b.farm.name
    } else if (sortBy === "cropType") {
      aValue = a.cropType ? a.cropType.name : a.customCropType ? a.customCropType.cropType : ""
      bValue = b.cropType ? b.cropType.name : b.customCropType ? b.customCropType.cropType : ""
    } else if (sortBy === "plantingDate" || sortBy === "endDate" || sortBy === "createdAt") {
      aValue = new Date(aValue || 0).getTime()
      bValue = new Date(bValue || 0).getTime()
    }

    if (sortOrder === "asc") {
      return aValue > bValue ? 1 : -1
    } else {
      return aValue < bValue ? 1 : -1
    }
  })

  // Get status class and icon
  const getStatusInfo = (status) => {
    switch (status.toLowerCase()) {
      case "active":
        return { class: "status-active", icon: "fa-play-circle", label: t("Active") }
      case "completed":
        return { class: "status-completed", icon: "fa-check-circle", label: t("Completed") }
      case "planning":
        return { class: "status-planning", icon: "fa-calendar-alt", label: t("Planning") }
      case "paused":
        return { class: "status-paused", icon: "fa-pause-circle", label: t("Paused") }
      default:
        return { class: "status-default", icon: "fa-info-circle", label: t(status) }
    }
  }

  if (loading) {
    return (
      <div className="project-management-loading">
        <div className="spinner"></div>
        <p>{t("Loading Fields...")}</p>
      </div>
    )
  }

  return (
    <div className="project-management-container" dir={direction}>
      <div className="project-management-header">
        <div className="header-content">
          <h1>{t("Field Management")}</h1>
          <p className="header-subtitle">
            {t("Manage all your Fields in one place")} • {filteredProjects.length} {t("Fields")}
          </p>
        </div>
        <div className="header-actions">
          <Link to="/add-project" className="add-project-button">
            <i className="fas fa-plus"></i> {t("Add New Field")}
          </Link>
        </div>
      </div>

      <div className="project-filters">
        <div className="filter-row">
          <div className="search-container">
            <div className="search-input-wrapper">
              <i className="fas fa-search search-icon"></i>
              <input
                type="text"
                placeholder={t("Search Fields by name, crop, farm...")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
          </div>

          <div className="filter-controls">
            <select value={selectedFarm} onChange={(e) => setSelectedFarm(e.target.value)} className="filter-select">
              <option value="all">{t("All Farms")}</option>
              {farms.map((farm) => (
                <option key={farm._id} value={farm._id}>
                  {farm.name}
                </option>
              ))}
            </select>

            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="filter-select">
              <option value="all">{t("All Status")}</option>
              <option value="active">{t("Active")}</option>
              <option value="planning">{t("Planning")}</option>
              <option value="paused">{t("Paused")}</option>
              <option value="completed">{t("Completed")}</option>
            </select>
          </div>
        </div>

        <div className="sort-controls">
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="sort-select">
            <option value="name">{t("Sort by Name")}</option>
            <option value="farm">{t("Sort by Farm")}</option>
            <option value="cropType">{t("Sort by Crop")}</option>
            <option value="plantingDate">{t("Sort by Planting Date")}</option>
            <option value="endDate">{t("Sort by End Date")}</option>
            <option value="createdAt">{t("Sort by Date Created")}</option>
          </select>
          <button
            onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
            className="sort-order-button"
            title={t(sortOrder === "asc" ? "Ascending" : "Descending")}
          >
            <i className={`fas fa-sort-${sortOrder === "asc" ? "up" : "down"}`}></i>
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <i className="fas fa-exclamation-circle"></i>
          <p>{t(error)}</p>
        </div>
      )}

      {!error && sortedProjects.length === 0 ? (
        <div className="no-projects">
          <div className="no-projects-icon">
            <i className="fas fa-seedling"></i>
          </div>
          <h2>{t("No Fields Found")}</h2>
          <p>
            {searchTerm || statusFilter !== "all" || selectedFarm !== "all"
              ? t("No Fields match your current filters")
              : t("You haven't created any Fields yet")}
          </p>
          <Link to="/add-project" className="create-project-button">
            <i className="fas fa-plus"></i> {t("Create Your First Field")}
          </Link>
        </div>
      ) : (
        <div className="projects-grid">
          {sortedProjects.map((project) => {
            const statusInfo = getStatusInfo(project.status)
            const cropName = project.cropType
              ? project.cropType.name
              : project.customCropType
                ? project.customCropType.cropType
                : t("Not specified")

            // Calculate progress
            let progress = 0
            if (project.status === "completed") {
              progress = 100
            } else if (project.plantingDate && project.endDate) {
              const start = new Date(project.plantingDate).getTime()
              const end = new Date(project.endDate).getTime()
              const now = Date.now()
              if (now >= end) {
                progress = 100
              } else if (now <= start) {
                progress = 0
              } else {
                progress = Math.round(((now - start) / (end - start)) * 100)
              }
            }

            return (
              <div key={project._id} className="project-card">
                <div className="project-card-header">
                  <div className={`project-status ${statusInfo.class}`}>
                    <i className={`fas ${statusInfo.icon}`}></i>
                    <span>{statusInfo.label}</span>
                  </div>
                  <div className="project-actions">
                    <div className="project-actions-dropdown">
                      <button className="dropdown-toggle">
                        <i className="fas fa-ellipsis-v"></i>
                      </button>
                      <div className="dropdown-menu">
                        <Link to={`/projects/${project._id}`} className="dropdown-item">
                          <i className="fas fa-eye"></i> {t("View Details")}
                        </Link>
                        <Link to={`/projects/${project._id}/edit`} className="dropdown-item">
                          <i className="fas fa-edit"></i> {t("Edit Field")}
                        </Link>
                        <div className="dropdown-divider"></div>
                        <div className="dropdown-submenu">
                          <button className="dropdown-item">
                            <i className="fas fa-sync-alt"></i> {t("Change Status")}{" "}
                            <i className="fas fa-chevron-right"></i>
                          </button>
                          <div className="dropdown-submenu-content">
                            <button
                              onClick={() => handleStatusChange(project._id, "active")}
                              className="dropdown-item"
                              disabled={project.status === "active"}
                            >
                              <i className="fas fa-play-circle"></i> {t("Active")}
                            </button>
                            <button
                              onClick={() => handleStatusChange(project._id, "planning")}
                              className="dropdown-item"
                              disabled={project.status === "planning"}
                            >
                              <i className="fas fa-calendar-alt"></i> {t("Planning")}
                            </button>
                            <button
                              onClick={() => handleStatusChange(project._id, "paused")}
                              className="dropdown-item"
                              disabled={project.status === "paused"}
                            >
                              <i className="fas fa-pause-circle"></i> {t("Paused")}
                            </button>
                            <button
                              onClick={() => handleStatusChange(project._id, "completed")}
                              className="dropdown-item"
                              disabled={project.status === "completed"}
                            >
                              <i className="fas fa-check-circle"></i> {t("Completed")}
                            </button>
                          </div>
                        </div>
                        <div className="dropdown-divider"></div>
                        <button
                          onClick={() => handleDeleteProject(project._id, project.name)}
                          className="dropdown-item delete-item"
                        >
                          <i className="fas fa-trash-alt"></i> {t("Delete")}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <Link to={`/projects/${project._id}`} className="project-card-content">
                  <h3 className="project-name">{project.name}</h3>
                  <div className="project-farm">
                    <i className="fas fa-map-marker-alt"></i> {project.farm.name}
                  </div>
                  <div className="project-crop">
                    <i className="fas fa-seedling"></i> {cropName}
                  </div>

                  {project.description && (
                    <div className="project-description">
                      {project.description.length > 100
                        ? `${project.description.substring(0, 100)}...`
                        : project.description}
                    </div>
                  )}

                  <div className="project-dates">
                    {project.plantingDate && (
                      <div className="project-date">
                        <i className="fas fa-calendar-plus"></i>
                        <span>
                          {t("Planted")}: {new Date(project.plantingDate).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                    {project.endDate && (
                      <div className="project-date">
                        <i className="fas fa-calendar-check"></i>
                        <span>
                          {t("End")}: {new Date(project.endDate).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                  </div>

                  {project.plantingDate && project.endDate && (
                    <div className="project-progress">
                      <div className="progress-bar">
                        <div className="progress-fill" style={{ width: `${progress}%` }}></div>
                      </div>
                      <div className="progress-text">
                        {progress}% {t("Complete")}
                      </div>
                    </div>
                  )}
                </Link>

                <div className="project-card-footer">
                  <div className="project-created">
                    <i className="fas fa-clock"></i>
                    {t("Created")}: {new Date(project.createdAt).toLocaleDateString()}
                  </div>
                  <Link to={`/projects/${project._id}`} className="view-details-button">
                    {t("View Details")} <i className="fas fa-arrow-right"></i>
                  </Link>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default ProjectManagement
